import { Avatar, Popper, styled } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Text } from '../../components/typography'
import { ProjectMemberSearchOption } from '../../../domain/value-object/ProjectMemberSearchOption'
import { colorPalette } from '../../style/colorPalette'
import { Color } from '../../../styles/commonStyles'

export type ProjectMemberPopperProps = {
  open: boolean
  anchorEl: HTMLElement | null
  options: ProjectMemberSearchOption[]
  onSelect?: (member: ProjectMemberSearchOption) => void
  inputRef: React.RefObject<HTMLInputElement>
  searchText: string
  offsetY?: number
}

export type ProjectMemberPopperHandle = {
  arrowDown: () => void
  arrowUp: () => void
  enter: () => boolean // returns true if a member was selected
}

type MemberOptionProps = {
  option: ProjectMemberSearchOption
  optionIndex: number
  isFocused: boolean
  onClick: () => void
  onMouseEnter: () => void
  isReleased?: boolean
}

const MemberOption = ({
  option,
  optionIndex,
  isFocused,
  onClick,
  onMouseEnter,
  isReleased = false,
}: MemberOptionProps) => (
  <MemberOptionRoot
    data-option-index={optionIndex}
    data-released={isReleased || undefined}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    isFocused={isFocused}
  >
    <MemberAvatar src={option.iconUrl ?? undefined} />
    {option.name}
  </MemberOptionRoot>
)

export const ProjectMemberPopper = forwardRef<
  ProjectMemberPopperHandle,
  ProjectMemberPopperProps
>(
  (
    { open, anchorEl, options, onSelect, inputRef, searchText, offsetY },
    ref
  ) => {
    const [focusedIndex, setFocusedIndex] = useState<number>(-1)
    const [displayReleased, setDisplayReleased] = useState<boolean>(false)
    const paperRef = useRef<HTMLDivElement>(null)

    // リリース済みを除外
    const members = useMemo(
      () => options.filter(option => !option.released),
      [options]
    )

    const released = useMemo(
      () => options.filter(option => option.released),
      [options]
    )

    const flatOptions = useMemo(() => {
      if (displayReleased) {
        return [...members, ...released]
      }
      return members
    }, [members, displayReleased, released])

    // アコーディオンヘッダーのインデックス（閉じている時のみ有効）
    const accordionHeaderIndex = useMemo(() => {
      if (displayReleased) return -1
      return members.length
    }, [members.length, displayReleased])

    // キーボードナビゲーション用の総アイテム数
    const totalNavigableItems = useMemo(() => {
      if (displayReleased || released.length === 0) {
        return flatOptions.length
      }
      // アコーディオンが閉じている時はヘッダーも含める
      return flatOptions.length + 1
    }, [flatOptions.length, displayReleased, released.length])

    const focusedOption = useMemo(() => {
      if (focusedIndex < 0 || focusedIndex >= flatOptions.length) return null
      return flatOptions[focusedIndex]
    }, [focusedIndex, flatOptions])

    const isAccordionHeaderFocused = useMemo(() => {
      return !displayReleased && focusedIndex === accordionHeaderIndex
    }, [displayReleased, focusedIndex, accordionHeaderIndex])

    useEffect(() => {
      setFocusedIndex(-1)
    }, [searchText])

    // フォーカスが当たっている要素が表示されるようにスクロール
    useEffect(() => {
      if (focusedIndex < 0 || !paperRef.current) return
      const focusedElement = paperRef.current.querySelector(
        `[data-option-index="${focusedIndex}"]`
      )
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest' })
      }
    }, [focusedIndex])

    // アコーディオンON時にリリース済みメンバーが見えるようにスクロール
    useLayoutEffect(() => {
      if (displayReleased && paperRef.current && released.length > 0) {
        const paper = paperRef.current

        if (released.length <= 5) {
          // 5人以下なら一番下までスクロール
          paper.scrollTop = paper.scrollHeight
        } else {
          // 6人以上なら2人目まで見えるようにスクロール
          const releasedElements = paper.querySelectorAll(
            '[data-released="true"]'
          )
          if (releasedElements.length >= 2) {
            releasedElements[2].scrollIntoView({ block: 'end' })
          }
        }
      }
    }, [displayReleased, released.length])

    const handleAccordionClick = useCallback(() => {
      setDisplayReleased(prev => {
        if (!prev) {
          setFocusedIndex(accordionHeaderIndex)
        }
        return !prev
      })
      inputRef.current?.focus({ preventScroll: true })
    }, [inputRef, accordionHeaderIndex])

    useImperativeHandle(
      ref,
      () => ({
        arrowDown: () => {
          setFocusedIndex(prev =>
            prev < totalNavigableItems - 1 ? prev + 1 : prev
          )
        },
        arrowUp: () => {
          setFocusedIndex(prev => (prev > 0 ? prev - 1 : prev))
        },
        enter: () => {
          if (isAccordionHeaderFocused) {
            handleAccordionClick()
            return false
          } else if (focusedOption) {
            onSelect?.(focusedOption)
            return true
          }
          return false
        },
      }),
      [
        totalNavigableItems,
        focusedOption,
        onSelect,
        isAccordionHeaderFocused,
        handleAccordionClick,
      ]
    )

    const handleOptionClick = useCallback(
      (option: ProjectMemberSearchOption) => {
        onSelect?.(option)
      },
      [onSelect]
    )

    const popperModifiers = useMemo(
      () =>
        offsetY
          ? [
              {
                name: 'offset',
                options: {
                  offset: [0, offsetY],
                },
              },
            ]
          : [],
      [offsetY]
    )

    return (
      <Popper
        open={open && anchorEl !== null}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={popperModifiers}
        style={{ zIndex: 1400 }}
      >
        <Paper ref={paperRef}>
          {members.length > 0 && (
            <MemberListArea>
              {members.map((option, index) => (
                <MemberOption
                  key={option.uuid}
                  option={option}
                  optionIndex={index}
                  isFocused={focusedOption?.uuid === option.uuid}
                  onClick={() => handleOptionClick(option)}
                  onMouseEnter={() => setFocusedIndex(index)}
                />
              ))}
            </MemberListArea>
          )}
          {released.length > 0 && (
            <AccordionHeader
              data-switch="released"
              data-option-index={
                !displayReleased ? accordionHeaderIndex : undefined
              }
              onClick={handleAccordionClick}
              onMouseEnter={() =>
                !displayReleased && setFocusedIndex(accordionHeaderIndex)
              }
              style={{
                color: isAccordionHeaderFocused ? Color.MAIN : undefined,
              }}
            >
              <ArrowIcon expanded={displayReleased} />
              <Text variant="s">リリース済み</Text>
            </AccordionHeader>
          )}
          {displayReleased &&
            released.length > 0 &&
            released.map(option => {
              const optionIndex = flatOptions.findIndex(
                o => o.uuid === option.uuid
              )
              return (
                <MemberOption
                  key={option.uuid}
                  option={option}
                  optionIndex={optionIndex}
                  isFocused={focusedOption?.uuid === option.uuid}
                  onClick={() => handleOptionClick(option)}
                  onMouseEnter={() => setFocusedIndex(optionIndex)}
                  isReleased={true}
                />
              )
            })}
        </Paper>
      </Popper>
    )
  }
)

const Paper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  backgroundColor: colorPalette.monotone[0],
  boxShadow: `0px 4px 16px 0px ${colorPalette.monotone[4]}80`,
  maxHeight: '300px',
  overflowY: 'auto',
  padding: '16px',
  borderRadius: '4px',
})

const MemberListArea = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
})

const AccordionHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '8px 0',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const ArrowIcon = styled(ChevronRightIcon)<{ expanded: boolean }>(
  ({ expanded }) => ({
    fontSize: '16px',
    transition: 'transform 0.2s',
    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
  })
)

const MemberAvatar = styled(Avatar)({
  width: '20px',
  height: '20px',
  marginRight: '5px',
})

const MemberOptionRoot = styled('div')<{
  isFocused: boolean
}>(({ isFocused }) => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'Noto Sans JP',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1,
  color: isFocused ? Color.MAIN : colorPalette.monotone[10],
}))
